import type { VeoPlugin } from '../../types';
import { hasWindow } from '../../utils/safe-env';
import { definePlugin } from '../plugin';
import { GuidesController } from './guides-controller';

export interface GuidesPluginConfig {
  apiUrl: string;
  apiKey: string;
  debug?: boolean;
}

/** Resultado del install — expone el controller para debugging desde el demo. */
export interface GuidesPluginHandle {
  controller: GuidesController;
}

/**
 * Plugin que renderiza guías in-app (modal/banner/tooltip) consultando
 * `GET /v1/guides/resolve` y registra las interacciones en
 * `POST /v1/guides/:id/interactions` vía queue dedicada con retry y
 * sendBeacon en pagehide.
 *
 * Opt-in: `init({ ..., guides: true })`. El plugin escucha
 * `pageview` e `identify` del client; el payload del evento ya trae
 * `sessionId` y `endUserId` para que no necesitemos consultar al
 * client en cada disparo.
 *
 * Frequency híbrida: el backend filtra autoritariamente; el cache
 * local (`GuideFrequencyCache`) solo previene el flash visual cuando
 * la interaction aún no se persistió.
 */
export function guidesPlugin(config: GuidesPluginConfig): VeoPlugin {
  return definePlugin({
    name: 'guides',
    install(client) {
      if (!hasWindow()) return;
      const controller = new GuidesController(client, config);

      // Exponer el controller en window para que el demo y tests E2E
      // puedan inspeccionar el cache y la queue.
      (window as unknown as { __veoGuides?: GuidesPluginHandle }).__veoGuides = { controller };

      client.on('identify', ({ sessionId }) => {
        void controller.checkGuides(window.location.href, sessionId);
      });

      client.on('pageview', ({ url, sessionId }) => {
        void controller.checkGuides(url, sessionId);
      });

      // Como el bundle de guías se carga PEREZOSAMENTE, el plugin puede
      // instalarse DESPUÉS del pageview/identify inicial — esos eventos ya
      // pasaron. Chequeamos la página actual ahora mismo para que la guía
      // aparezca apenas se entra (sin necesitar una 2ª acción). Si todavía no
      // hay endUserId, checkGuides no hace nada y el listener de `identify` la
      // mostrará; el dedupe por guideId evita un doble render.
      void controller.checkGuides(window.location.href, client.getSessionId());
    },
  });
}

export type { PreviewGuideInput, PreviewHandle, PreviewResult } from './guide-preview';
export { closeGuidePreview, previewGuide } from './guide-preview';
export type { PreviewModeHandle, PreviewModeOptions } from './preview-mode';
export { runPreviewMode } from './preview-mode';
export type { GuideInteractionEvent, GuideStep, ResolvedGuide } from './types';
